import { useSearchParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { SearchInput, Select } from '@/components/ui/Input'
import { Pagination, PageInfo } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { useAgencies } from '@/hooks/useAgencies'
import { PROVINCE_NAMES } from '@/lib/constants'
import type { Agency } from '@/lib/pocketbase'

export function AgenciesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') || ''
  const province = searchParams.get('province') || ''
  const page = parseInt(searchParams.get('page') || '1')

  const { data, isLoading, error } = useAgencies({ search, province, page, perPage: 20 })

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('q', value)
    } else {
      params.delete('q')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handleProvinceChange = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('province', value)
    } else {
      params.delete('province')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  return (
    <div>
      <PageHeader
        title="หน่วยงาน"
        description="เรียกดูหน่วยงานภาครัฐและกิจกรรมการจัดซื้อจัดจ้าง"
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <SearchInput
          placeholder="ค้นหาตามชื่อหน่วยงาน..."
          defaultValue={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="flex-1 max-w-md"
        />
        <Select
          value={province}
          onChange={(e) => handleProvinceChange(e.target.value)}
          className="w-48"
        >
          <option value="">ทุกจังหวัด</option>
          {Object.entries(PROVINCE_NAMES).map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </Select>
      </div>

      {/* Results info */}
      {data && (
        <div className="mb-4">
          <PageInfo
            currentPage={data.page}
            pageSize={data.perPage}
            totalItems={data.totalItems}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-48 bg-slate-200 rounded" />
                  <div className="h-3 w-32 bg-slate-200 rounded" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <Card className="p-6 text-center">
          <Icon icon="solar:danger-circle-line-duotone" className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">โหลดหน่วยงานไม่สำเร็จ</h3>
          <p className="text-sm text-slate-500 mt-1">กรุณาลองใหม่ภายหลัง</p>
        </Card>
      )}

      {/* Empty */}
      {data && data.items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon icon="solar:buildings-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">ไม่พบหน่วยงาน</h3>
          <p className="text-sm text-slate-500 mt-1">
            ลองปรับคำค้นหาหรือตัวกรอง
          </p>
        </Card>
      )}

      {/* Agency list */}
      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((agency) => (
              <AgencyCard key={agency.id} agency={agency} showDetails />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  )
}

function AgencyCard({ agency, showDetails }: { agency: Agency; showDetails: boolean }) {
  return (
    <Link to={`/agencies/${agency.id}`}>
      <Card hover className="group">
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className="w-12 h-12 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
            <Icon icon="solar:buildings-line-duotone" className="w-6 h-6" />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-medium text-slate-900 truncate group-hover:text-blue-600 transition-colors">
              {agency.name}
            </h3>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              {agency.province_code && (
                <span className="flex items-center gap-1.5">
                  <Icon icon="solar:map-point-line-duotone" className="w-4 h-4 text-slate-400" />
                  {PROVINCE_NAMES[agency.province_code] || agency.province_code}
                </span>
              )}
              {showDetails && agency.dept_code && (
                <span className="font-mono text-xs">{agency.dept_code}</span>
              )}
            </div>
          </div>

          {/* Badge */}
          {agency.ministry && (
            <Badge variant="secondary" className="hidden sm:flex">
              {agency.ministry}
            </Badge>
          )}

          {/* Arrow */}
          <Icon icon="solar:alt-arrow-right-line-duotone" className="w-5 h-5 text-slate-300" />
        </div>
      </Card>
    </Link>
  )
}
